import React, { useRef, useState, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { CircularProgress, Checkbox, FormControlLabel } from '@mui/material'
import { H2, Line, Button, Flex } from '../../lib/components'
import { DeleteIcon, FileUploadIcon } from '../../lib/components/Icon'
import { NewsletterSendFormData } from '../../services/newsletter-service'
import { NewsletterGroup, AttachmentFile } from './newsletter-send-form-container'
import { renderPreview } from './utils'
import './newsletter-send-form.scss'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type HeaderTemplateValue = 'NONE' | 'STANDARD' | 'MINIMAL' | 'LOGO'
export type FooterTemplateValue = 'NONE' | 'STANDARD' | 'MINIMAL' | 'LOGO'

const HEADER_TEMPLATES: Readonly<{ value: HeaderTemplateValue; label: string }[]> = [
  { value: 'NONE', label: 'No Header' },
  { value: 'STANDARD', label: 'Standard Header (Logo + Subject)' },
  { value: 'MINIMAL', label: 'Minimal Header (Subject only)' },
  { value: 'LOGO', label: 'Logo only' },
] as const

const FOOTER_TEMPLATES: Readonly<{ value: FooterTemplateValue; label: string }[]> = [
  { value: 'NONE', label: 'No Footer' },
  { value: 'STANDARD', label: 'Standard Footer (Logo + Footer Text)' },
  { value: 'MINIMAL', label: 'Minimal Footer (Footer Text only)' },
  { value: 'LOGO', label: 'Logo only' },
] as const

type FromEmailValue = 'alerts@credibila.com' | 'support@credibila.com' | 'info@credibila.com'

const FROM_EMAILS: Readonly<{ value: FromEmailValue; label: string }[]> = [
  { value: 'alerts@credibila.com', label: 'Internal Alerts (alerts@credibila.com)' },
  { value: 'support@credibila.com', label: 'Client Support (support@credibila.com)' },
  { value: 'info@credibila.com', label: 'ONS support (info@credibila.com)' },
] as const

type NewsletterSendFormPresenterProps = {
  form: UseFormReturn<NewsletterSendFormData>
  groups: NewsletterGroup[]
  isLoadingGroups: boolean
  isSubmitting: boolean
  isUploading: boolean
  attachments: AttachmentFile[]
  showPreview: boolean
  onSubmit: (data: NewsletterSendFormData) => Promise<void>
  onAttachmentUpload: (file: File) => Promise<void>
  onRemoveAttachment: (name: string) => void
  onTogglePreview: () => void
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  handleLoadMore: () => void
}

function NewsletterSendFormPresenter({
  form,
  groups,
  isLoadingGroups,
  isSubmitting,
  isUploading,
  attachments,
  showPreview,
  onSubmit,
  onAttachmentUpload,
  onRemoveAttachment,
  onTogglePreview,
  pagination,
  handleLoadMore,
}: NewsletterSendFormPresenterProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = form

  const includeSubscribers = watch('includeSubscribers')
  const selectedGroupIds = watch('groupIds')
  const body = watch('body')
  const subject = watch('subject')
  const headerTemplate = watch('headerTemplate')
  const footerTemplate = watch('footerTemplate')
  const replyTo = watch('replyTo')
  const fromEmail = watch('fromEmail')
  const ccEmails = watch('cc')
  const bccEmails = watch('bcc')

  const [ccInput, setCcInput] = useState('')
  const [ccError, setCcError] = useState('')
  const [bccInput, setBccInput] = useState('')
  const [bccError, setBccError] = useState('')

  const fileInputRef = useRef<HTMLInputElement>(null)
  const quillRef = useRef<ReactQuill>(null)

  const handleIncludeSubscribersToggle = () => {
    setValue('includeSubscribers', !includeSubscribers, { shouldDirty: true, shouldValidate: true })
    clearErrors('includeSubscribers')
  }

  const handleGroupToggle = (groupId: string) => {
    const current = selectedGroupIds || []
    const next = current.includes(groupId)
      ? current.filter((id) => id !== groupId)
      : [...current, groupId]
    setValue('groupIds', next, { shouldDirty: true, shouldValidate: true })
    clearErrors('groupIds')
  }

  const handleSelectAllGroups = () => {
    if (selectedGroupIds.length === groups.length) {
      setValue('groupIds', [], { shouldDirty: true })
    } else {
      setValue(
        'groupIds',
        groups.map((g) => g.id),
        { shouldDirty: true },
      )
      clearErrors('groupIds')
    }
  }

  const addEmailToField = (
    field: 'cc' | 'bcc',
    raw: string,
    setInput: (v: string) => void,
    setError: (v: string) => void,
  ) => {
    const email = raw.trim().toLowerCase()
    if (!email) return
    if (!EMAIL_REGEX.test(email)) {
      setError('Enter a valid email address')
      return
    }
    const current = field === 'cc' ? ccEmails : bccEmails
    if (current.includes(email)) {
      setError('This email has already been added')
      return
    }
    setValue(field, [...current, email], { shouldDirty: true })
    clearErrors(field)
    setInput('')
    setError('')
  }

  const removeEmailFromField = (field: 'cc' | 'bcc', emailToRemove: string) => {
    const current = field === 'cc' ? ccEmails : bccEmails
    setValue(
      field,
      current.filter((e) => e !== emailToRemove),
      { shouldDirty: true },
    )
  }

  const handleEmailKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: 'cc' | 'bcc',
    input: string,
    setInput: (v: string) => void,
    setError: (v: string) => void,
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addEmailToField(field, input, setInput, setError)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onAttachmentUpload(file)
    e.target.value = ''
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          ['clean'],
        ],
      },
      clipboard: { matchVisual: false },
    }),
    [],
  )

  const quillFormats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'indent',
    'blockquote',
    'code-block',
    'link',
    'image',
  ]

  const totalRecipients = groups
    .filter((g) => selectedGroupIds.includes(g.id))
    .reduce((sum, g) => sum + g.emailCount, 0)

  return (
    <>
      {(isSubmitting || isLoadingGroups) && (
        <div className='loading-overlay'>
          <CircularProgress size={40} color='primary' className='loading-overlay__spinner' />
        </div>
      )}

      <Flex variant='column-center'>
        <H2 classes={['mb-24']} text='Send Newsletter' />

        <form className='send-form' onSubmit={handleSubmit(onSubmit)} noValidate>
          <Line />

          {/* Newsletter Groups */}
          <div className='send-form__field'>
            <label className='send-form__label'>
              Recipient Groups <span className='send-form__required'>*</span>
            </label>
            <div className='send-form__groups-wrapper'>
              <div className='send-form__groups-header'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={groups.length > 0 && selectedGroupIds.length === groups.length}
                      indeterminate={
                        selectedGroupIds.length > 0 && selectedGroupIds.length < groups.length
                      }
                      onChange={handleSelectAllGroups}
                      size='small'
                      sx={{ '&.Mui-checked': { color: '#48b767' } }}
                    />
                  }
                  label={<span className='send-form__groups-select-all'>Select All</span>}
                />
                {selectedGroupIds.length > 0 && (
                  <span className='send-form__groups-count'>
                    {selectedGroupIds.length} group{selectedGroupIds.length > 1 ? 's' : ''} selected
                    &middot; {totalRecipients} recipient{totalRecipients !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className='send-form__groups-list'>
                {groups.length === 0 && !isLoadingGroups && (
                  <p className='send-form__groups-empty'>No groups found. Create a group first.</p>
                )}
                {groups.map((group) => (
                  <label key={group.id} className='send-form__group-item'>
                    <Checkbox
                      checked={selectedGroupIds.includes(group.id)}
                      onChange={() => handleGroupToggle(group.id)}
                      size='small'
                      sx={{ '&.Mui-checked': { color: '#48b767' } }}
                    />
                    <div className='send-form__group-info'>
                      <span className='send-form__group-name'>{group.name}</span>
                      <span className='send-form__group-meta'>
                        {group.emailCount} email{group.emailCount !== 1 ? 's' : ''}
                        {group.description && ` — ${group.description}`}
                      </span>
                    </div>
                  </label>
                ))}
                {pagination.total > pagination.count + pagination.offset && (
                  <div className='send-form__load-more-container'>
                    <Button
                      buttonType='button'
                      variant='primary-btn'
                      isDisabled={isLoadingGroups}
                      handleClick={handleLoadMore}
                      text='Load More'
                      classes={['send-form__load-more-button']}
                    />
                  </div>
                )}
              </div>
            </div>
            {errors.groupIds && <span className='send-form__error'>{errors.groupIds.message}</span>}
          </div>

          {/* Include Subscribers */}
          <div className='send-form__field'>
            <div className='send-form__checkbox-wrapper'>
              <Checkbox
                checked={includeSubscribers}
                onChange={handleIncludeSubscribersToggle}
                size='medium'
                sx={{ '&.Mui-checked': { color: '#48b767' } }}
              />
              <label className='send-form__checkbox-label'>
                Include All Newsletter Subscribers
              </label>
            </div>
            <p className='send-form__hint'>
              Note: By default, only the selected groups will be included in the email. If you want
              to include all newsletter subscribers, check this box.
            </p>
            {errors.includeSubscribers && (
              <span className='send-form__error'>{errors.includeSubscribers.message}</span>
            )}
          </div>

          <Line />

          {/* Subject */}
          <div className='send-form__field'>
            <label className='send-form__label' htmlFor='subject'>
              Subject <span className='send-form__required'>*</span>
            </label>
            <input
              id='subject'
              className={`send-form__input ${errors.subject ? 'send-form__input--error' : ''}`}
              placeholder='Enter email subject'
              maxLength={500}
              {...register('subject', {
                required: 'Subject is required',
                maxLength: { value: 500, message: 'Subject must be at most 500 characters' },
              })}
            />
            {errors.subject && <span className='send-form__error'>{errors.subject.message}</span>}
          </div>

          {/* From Email */}
          <div className='send-form__field'>
            <label className='send-form__label' htmlFor='fromEmail'>
              From Email
            </label>
            <select
              id='fromEmail'
              className={`send-form__select ${errors.fromEmail ? 'send-form__select--error' : ''}`}
              {...register('fromEmail', {
                required: 'From email is required',
                validate: (value) => {
                  if (!FROM_EMAILS.some((email) => email.value === value)) {
                    return 'Invalid from email'
                  }
                  return true
                },
              })}
            >
              <option value='' disabled hidden>
                Select from email
              </option>
              {FROM_EMAILS.map((email) => (
                <option key={email.value} value={email.value}>
                  {email.label}
                </option>
              ))}
            </select>
            <span className='send-form__hint'>
              The email address that will be used as the sender of the newsletter.
            </span>
            {errors.fromEmail && (
              <span className='send-form__error'>{errors.fromEmail.message}</span>
            )}
          </div>

          {/* Reply-To */}
          <div className='send-form__field'>
            <label className='send-form__label' htmlFor='replyTo'>
              Reply-To Email
            </label>
            <input
              id='replyTo'
              type='email'
              className={`send-form__input ${errors.replyTo ? 'send-form__input--error' : ''}`}
              placeholder='noreply@example.com'
              {...register('replyTo')}
            />
            <span className='send-form__hint'>
              Replies from recipients will be sent to this address.
            </span>
            {errors.replyTo && <span className='send-form__error'>{errors.replyTo.message}</span>}
          </div>

          {/* CC */}
          <div className='send-form__field'>
            <label className='send-form__label'>CC</label>
            <div
              className={`send-form__email-chips-wrapper ${
                errors.cc || ccError ? 'send-form__email-chips-wrapper--error' : ''
              }`}
            >
              {ccEmails.length > 0 && (
                <div className='send-form__email-chips'>
                  {ccEmails.map((email) => (
                    <span key={email} className='send-form__email-chip'>
                      {email}
                      <span
                        className='send-form__email-chip-remove'
                        onClick={() => removeEmailFromField('cc', email)}
                      >
                        <DeleteIcon dataTestId={`remove-cc-${email}`} />
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <input
                className='send-form__email-chip-input'
                placeholder='Type CC email and press Enter'
                value={ccInput}
                onChange={(e) => {
                  setCcInput(e.target.value)
                  setCcError('')
                }}
                onKeyDown={(e) => handleEmailKeyDown(e, 'cc', ccInput, setCcInput, setCcError)}
                onBlur={() => {
                  if (ccInput.trim()) addEmailToField('cc', ccInput, setCcInput, setCcError)
                }}
              />
            </div>
            {ccError && <span className='send-form__error'>{ccError}</span>}
            {errors.cc && <span className='send-form__error'>{errors.cc.message}</span>}
          </div>

          {/* BCC */}
          <div className='send-form__field'>
            <label className='send-form__label'>BCC</label>
            <div
              className={`send-form__email-chips-wrapper ${
                errors.bcc || bccError ? 'send-form__email-chips-wrapper--error' : ''
              }`}
            >
              {bccEmails.length > 0 && (
                <div className='send-form__email-chips'>
                  {bccEmails.map((email) => (
                    <span key={email} className='send-form__email-chip'>
                      {email}
                      <span
                        className='send-form__email-chip-remove'
                        onClick={() => removeEmailFromField('bcc', email)}
                      >
                        <DeleteIcon dataTestId={`remove-bcc-${email}`} />
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <input
                className='send-form__email-chip-input'
                placeholder='Type BCC email and press Enter'
                value={bccInput}
                onChange={(e) => {
                  setBccInput(e.target.value)
                  setBccError('')
                }}
                onKeyDown={(e) => handleEmailKeyDown(e, 'bcc', bccInput, setBccInput, setBccError)}
                onBlur={() => {
                  if (bccInput.trim()) addEmailToField('bcc', bccInput, setBccInput, setBccError)
                }}
              />
            </div>
            {bccError && <span className='send-form__error'>{bccError}</span>}
            {errors.bcc && <span className='send-form__error'>{errors.bcc.message}</span>}
          </div>

          <Line />

          {/* Header & Footer Templates */}
          <div className='send-form__row'>
            <div className='send-form__field send-form__field--half'>
              <label className='send-form__label' htmlFor='headerTemplate'>
                Header Template
              </label>
              <select
                id='headerTemplate'
                className='send-form__select'
                {...register('headerTemplate')}
              >
                {HEADER_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className='send-form__field send-form__field--half'>
              <label className='send-form__label' htmlFor='footerTemplate'>
                Footer Template
              </label>
              <select
                id='footerTemplate'
                className='send-form__select'
                {...register('footerTemplate')}
              >
                {FOOTER_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Email Body */}
          <div className='send-form__field'>
            <label className='send-form__label'>
              Email Body <span className='send-form__required'>*</span>
            </label>
            <div className='send-form__editor-wrapper'>
              <ReactQuill
                ref={quillRef}
                theme='snow'
                value={body}
                onChange={(content) => {
                  setValue('body', content, { shouldDirty: true, shouldValidate: true })
                  clearErrors('body')
                }}
                modules={quillModules}
                formats={quillFormats}
                placeholder='Compose your email content here...'
              />
            </div>
            <span className='send-form__hint'>
              Use the toolbar to format text, insert images, add links, and more. You can also paste
              content from other sources.
            </span>
            {errors.body && <span className='send-form__error'>{errors.body.message}</span>}
          </div>

          {/* Attachments */}
          <div className='send-form__field'>
            <label className='send-form__label'>Attachments</label>
            <div className='send-form__attachments'>
              {attachments.map((att) => (
                <div key={att.name} className='send-form__attachment-item'>
                  <div className='send-form__attachment-info'>
                    <span className='send-form__attachment-name'>{att.name}</span>
                    <span className='send-form__attachment-size'>{formatFileSize(att.size)}</span>
                  </div>
                  <span
                    className='send-form__attachment-remove'
                    onClick={() => onRemoveAttachment(att.name)}
                  >
                    <DeleteIcon dataTestId={`remove-attachment-${att.name}`} />
                  </span>
                </div>
              ))}
              <div
                className='send-form__attachment-upload'
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    <FileUploadIcon />
                    <span>Add Attachment</span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type='file'
                className='send-form__file-hidden'
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </div>
            <span className='send-form__hint'>Max file size: 10 MB per attachment.</span>
          </div>

          <Line />

          {/* Actions */}
          <div className='send-form__actions'>
            <Button
              buttonType='button'
              variant='secondary-btn'
              handleClick={onTogglePreview}
              text={showPreview ? 'Hide Preview' : 'Preview Email'}
            />
            <Button
              buttonType='submit'
              variant='primary-btn'
              handleClick={() => {
                if (!body || body === '<p><br></p>') {
                  form.setError('body', { type: 'manual', message: 'Email body is required' })
                }
              }}
              text={isSubmitting ? 'Sending...' : 'Send Newsletter'}
              isDisabled={isSubmitting}
            />
          </div>
        </form>

        {/* Preview Modal */}
        {showPreview && (
          <div className='send-form__preview-overlay' onClick={onTogglePreview}>
            <div className='send-form__preview-modal' onClick={(e) => e.stopPropagation()}>
              <div className='send-form__preview-header'>
                <h3>Email Preview</h3>
                <div className='send-form__preview-meta'>
                  <span>
                    <strong>From:</strong> {fromEmail}
                  </span>
                  <span>
                    <strong>To:</strong> {totalRecipients} recipient
                    {totalRecipients !== 1 ? 's' : ''} in {selectedGroupIds.length} group
                    {selectedGroupIds.length !== 1 ? 's' : ''}
                  </span>
                  {replyTo && (
                    <span>
                      <strong>Reply-To:</strong> {replyTo}
                    </span>
                  )}
                  {ccEmails.length > 0 && (
                    <span>
                      <strong>CC:</strong> {ccEmails.join(', ')}
                    </span>
                  )}
                  {bccEmails.length > 0 && (
                    <span>
                      <strong>BCC:</strong> {bccEmails.join(', ')}
                    </span>
                  )}
                  {attachments.length > 0 && (
                    <span>
                      <strong>Attachments:</strong> {attachments.map((a) => a.name).join(', ')}
                    </span>
                  )}
                </div>
                <button className='send-form__preview-close' onClick={onTogglePreview}>
                  &times;
                </button>
              </div>
              <div className='send-form__preview-body'>
                <div
                  className='send-form__preview-content'
                  dangerouslySetInnerHTML={{
                    __html: renderPreview(headerTemplate, footerTemplate, subject, body),
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
    </>
  )
}

export { NewsletterSendFormPresenter, FROM_EMAILS, type FromEmailValue }
